import { Box, Typography, useTheme } from '@mui/material';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import {
  selectAutoPlayDirection,
  selectAutoPlayIncrement,
  selectAutoPlaySpeed,
  setAutoPlayDirection,
  setAutoPlayIncrement,
  setAutoPlaySpeed,
} from './imageSlice';
interface Props {
  isTooltipOpen: boolean;
}

const AutoplayButtonPopup: React.FC<Props> = ({ isTooltipOpen }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const autoPlayDirection = useAppSelector(selectAutoPlayDirection);
  const autoPlaySpeed = useAppSelector(selectAutoPlaySpeed);
  const [playSpeedLocal, setPlaySpeedLocal] = useState(autoPlaySpeed);
  const autoPlayIncrement = useAppSelector(selectAutoPlayIncrement);
  const [autoPlayIncrementLocal, setAutoPlayIncrementLocal] =
    useState(autoPlayIncrement);
  const [isTooltipOpenLocal, setIsTooltipOpenLocal] = useState(false);

  // If the tooltip is open in the parent, delay the local open state to allow the slider to hide it's own value tooltip until the parent tooltip is fully animated open
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsTooltipOpenLocal(isTooltipOpen);
    }, 500);
    return () => clearTimeout(timeout);
  }, [isTooltipOpen]);

  const handleDirectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setAutoPlayDirection(event.target.checked ? 'forward' : 'back'));
  };

  const handleSpeedSliderChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    if (typeof newValue === 'number') {
      setPlaySpeedLocal(newValue);
    }
  };

  const handleSpeedSliderCommit = () => {
    dispatch(setAutoPlaySpeed(playSpeedLocal));
  };

  const handleIncrementSliderChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    if (typeof newValue === 'number') {
      setAutoPlayIncrementLocal(newValue);
    }
  };

  const handleIncrementSliderCommit = () => {
    dispatch(setAutoPlayIncrement(autoPlayIncrementLocal));
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: '1rem',
      }}
    >
      <Typography
        variant='body2'
        sx={{ color: theme.colors.darkTheme.grey.muted }}
      >
        {t('Autoplay Direction', { ns: 'common' })}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body2'
          sx={{
            color:
              autoPlayDirection === 'back'
                ? theme.colors.common.white
                : theme.colors.darkTheme.grey.muted,
          }}
        >
          {t('Back', { ns: 'common' })}
        </Typography>
        <Switch
          checked={autoPlayDirection === 'forward'}
          onChange={handleDirectionChange}
          inputProps={{ 'aria-label': 'Autoplay direction' }}
        />
        <Typography
          variant='body2'
          sx={{
            color:
              autoPlayDirection === 'forward'
                ? theme.colors.common.white
                : theme.colors.darkTheme.grey.muted,
          }}
        >
          {t('Forward', { ns: 'common' })}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: theme.colors.darkTheme.grey.muted, mt: '0.5rem' }}
        >
          {t('Image Increment', { ns: 'common' })}
        </Typography>
        <Slider
          value={autoPlayIncrementLocal}
          aria-label='Auto play speed'
          defaultValue={autoPlayIncrementLocal}
          valueLabelDisplay={isTooltipOpenLocal ? 'auto' : 'off'}
          step={1}
          min={1}
          max={10}
          onChange={handleIncrementSliderChange}
          onChangeCommitted={handleIncrementSliderCommit}
          sx={{ width: '90%' }}
        />
        <Typography
          variant='body2'
          sx={{ color: theme.colors.darkTheme.grey.muted, mt: '0.5rem' }}
        >
          {t('Autoplay Speed (ms)', { ns: 'common' })}
        </Typography>
        <Slider
          value={playSpeedLocal}
          aria-label='Auto play speed'
          defaultValue={playSpeedLocal}
          valueLabelDisplay={isTooltipOpenLocal ? 'auto' : 'off'}
          step={1}
          min={200}
          max={5000}
          onChange={handleSpeedSliderChange}
          onChangeCommitted={handleSpeedSliderCommit}
          sx={{ width: '90%' }}
        />
      </Box>
    </Box>
  );
};

export default AutoplayButtonPopup;
